/**
 * Qual mensagem do usuário o índice do trilho acende como "atual", a partir
 * da geometria do scroll. Pura: o `messages.vue` mede o DOM e entrega números.
 *
 * A regra é a "última cujo topo já cruzou o centro da área visível" — a mais
 * próxima do centro entre as que o leitor já alcançou. Não é a de menor
 * distância absoluta: no fim de uma resposta longa, a pergunta SEGUINTE (ainda
 * abaixo da dobra) ficaria mais perto do centro do que a pergunta que se está
 * lendo, e o traço aceso pularia para algo que ninguém leu.
 *
 * ★ Com o scroll no FIM, a ativa é a ÚLTIMA — curto-circuito que a regra do
 * centro não alcança sozinha. Medido em 11/09/2026 na `/home-ia`, viewport de
 * 1440×1080 (container de 915px, centro em y=502): três perguntas curtas em
 * sequência, a última seguida só da resposta e do composer, scroll no fim e as
 * três à vista — a última ficava com o topo em y=583, abaixo do centro, e o
 * traço aceso parava na penúltima. Como o que existe ABAIXO dela (resposta
 * curta) é menor que meia área visível, o scroll nunca consegue trazê-la até
 * o centro: a distância do topo dela ao fundo do conteúdo é fixa (~376px), e
 * `topo <= centro` só vale enquanto `altura <= 2 × 376` — a 900px de janela
 * passava por 9px, a 1080px não. Quem está no fim está lendo a última: tudo o
 * que vem depois dela é a resposta dela.
 */

// Folga sub-pixel para "está no fim": `scrollTop` pode ser fracionário e o
// browser arredonda `scrollHeight` — sem folga, o último pixel nunca fecha.
export const TOLERANCIA_DO_FIM_PX = 2

/**
 * @param {Object} geometria — tudo em pixels, no sistema do CONTEÚDO rolável.
 * @param {number} geometria.scrollTop — quanto o container já rolou.
 * @param {number} geometria.clientHeight — altura da área visível.
 * @param {number} geometria.scrollHeight — altura do conteúdo rolável (o
 *   `scrollHeight` do container, ou o fim do conteúdo REAL quando há um spacer
 *   inflando o container — ver `messages.vue`).
 * @param {number[]} geometria.offsets — topo de cada mensagem do usuário, na
 *   ordem da conversa, relativo ao início do conteúdo (`scrollTop = 0`).
 * @param {number} [geometria.tolerancia] — a quantos pixels do fim ainda conta
 *   como "no fim". Default sub-pixel; o trilho passa a folga de "grudado no fim".
 * @returns {number} índice da ativa em `offsets`, ou `-1` sem mensagens.
 */
export function mensagemAtivaPorScroll({
  scrollTop = 0,
  clientHeight = 0,
  scrollHeight = 0,
  offsets = [],
  tolerancia = TOLERANCIA_DO_FIM_PX,
} = {}) {
  if (!Array.isArray(offsets) || !offsets.length) return -1
  const ultima = offsets.length - 1
  if (estaNoFim({ scrollTop, clientHeight, scrollHeight, tolerancia })) return ultima
  const centro = scrollTop + clientHeight / 2
  let ativa = 0
  offsets.forEach(function (offset, indice) {
    if (offset <= centro) ativa = indice
  })
  return ativa
}

function estaNoFim({ scrollTop, clientHeight, scrollHeight, tolerancia }) {
  return scrollTop + clientHeight >= scrollHeight - tolerancia
}
